import React, { useState } from "react";
import { Star } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/context/PageMetaContext";
import { useFormData } from "@/stores/formStore";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface FeedbackTabProps {
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
  hideTab?: boolean;
}

const FeedbackTab: React.FC<FeedbackTabProps> = ({ externalOpen, onExternalOpenChange, hideTab = false }) => {
  const { t } = useTranslation("widgets");
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onExternalOpenChange || setInternalOpen;
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { meta } = usePageMeta();
  const { formData } = useFormData();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(t("feedback.selectRating"));
      return;
    }
    // Honeypot: real users can't see this field. If it's filled, silently drop.
    if (honeypot.trim() !== "") {
      toast.success(t("feedback.thankYou"));
      setRating(0);
      setMessage("");
      setHoneypot("");
      setOpen(false);
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("feedback").insert({
      rating,
      message: message.trim() || null,
      page: meta.title || null,
      era: formData.era || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(t("feedback.submitFailed"));
      return;
    }
    toast.success(t("feedback.thankYou"));
    setRating(0);
    setMessage("");
    setHoneypot("");
    setOpen(false);
  };

  return (
    <>
      {/* Fixed vertical tab on right edge */}
      {!hideTab && <button
        onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-50 bg-background text-foreground border border-border border-r-0 px-2 py-3 rounded-l-md shadow-lg hover:bg-secondary transition-colors"
        style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
        aria-label={t("feedback.openTab")}
      >
        <span className="text-xs font-semibold tracking-wider uppercase">{t("feedback.openTab")}</span>
      </button>}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-background/95 backdrop-blur-sm border-border w-[340px] sm:max-w-[340px]">
          <SheetHeader>
            <SheetTitle className="font-antiqua text-foreground">{t("feedback.title")}</SheetTitle>
            <SheetDescription>{t("feedback.description")}</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Star rating */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">{t("feedback.rating")}</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={star === 1 ? t("feedback.rateAriaSingular", { n: star }) : t("feedback.rateAria", { n: star })}
                  >
                    <Star
                      className={`h-7 w-7 transition-colors ${
                        star <= (hoveredStar || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted-foreground/40"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t("feedback.comments")} <span className="text-muted-foreground font-normal">{t("feedback.optional")}</span>
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("feedback.placeholder")}
                className="resize-none bg-background/50"
                rows={4}
                maxLength={1000}
              />
            </div>

          {/* Honeypot field — hidden from real users, irresistible to bots */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="website-url">Website</label>
            <input
              id="website-url"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

            {/* Context info */}
            <p className="text-xs text-muted-foreground">
              {t("feedback.context", { page: meta.title || "—", era: formData.era || "—" })}
            </p>

            <Button
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className="w-full bg-brand-cream text-primary-foreground hover:bg-brand-cream/85"
            >
              {submitting ? t("feedback.sending") : t("feedback.submit")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default FeedbackTab;
